import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import { MarginAmount, MarginFormValues, MarginItem } from '../types';

export const exchangeMarginsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMargins: build.query<Response<MarginItem>, string>({
      query: (query) => ({
        url: `/v1/margins${query}&createType=all&excludeMarginsWithOrganization=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDefaultMargin: build.query<{ data: MarginItem }, void>({
      query: () => ({
        url: '/v1/margins/default',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMargin: build.query<{ data: MarginItem }, string>({
      query: (id) => ({
        url: `/v1/margins/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateDefaultMargin: build.mutation<
      {
        data: MarginItem;
      },
      { body: Partial<MarginFormValues> }
    >({
      query: (data) => ({
        url: '/v1/margins/default',
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateMargin: build.mutation<
      {
        data: MarginItem;
      },
      { body: Partial<MarginFormValues>; id: string }
    >({
      query: (data) => ({
        url: `/v1/margins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAmount: build.mutation<
      {
        data: MarginItem;
      },
      { body: MarginAmount }
    >({
      query: (data) => ({
        url: '/v1/margins-amounts',
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createAmounts: build.mutation<
      {
        data: MarginItem;
      },
      { body: { items: MarginAmount[] } }
    >({
      query: (data) => ({
        url: '/v1/margins-amounts/batch-create',
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateAmount: build.mutation<
      {
        data: MarginItem;
      },
      { body: MarginAmount; id: string }
    >({
      query: (data) => ({
        url: `/v1/margins-amounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteAmount: build.mutation<
      {
        data: MarginAmount;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/margins-amounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    restoreToDefault: build.mutation<
      {
        data: MarginItem;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/margins/${id}/restore`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetMarginsQuery,
  useLazyGetDefaultMarginQuery,
  useUpdateDefaultMarginMutation,
  useCreateAmountsMutation,
  useDeleteAmountMutation,
  useLazyGetMarginQuery,
  useUpdateMarginMutation,
  useCreateAmountMutation,
  useUpdateAmountMutation,
  useRestoreToDefaultMutation,
} = exchangeMarginsApi;

export const {
  endpoints: {
    getMargins,
    getDefaultMargin,
    updateDefaultMargin,
    createAmounts,
    deleteAmount,
    getMargin,
    updateMargin,
    updateAmount,
    restoreToDefault,
  },
} = exchangeMarginsApi;
